import axios from 'axios';
import { auth } from '../../../firebase';
import { cleanJsonEmptyValues } from '../../../utils/formatUtils';
import {
  Claim,
  ClaimStatus,
  ClaimSummary,
  DashboardClaimPolicySummary,
  NewClaimArgs,
  UpdateClaimArgs,
} from '../../Interfaces/Insurance/Claim';
import { Company } from '../../Interfaces/admin/User.interfaces';

const apiUrl = process.env['NX_API_URL'];

export const fetchClaims = async (
  company: Company,
  status?: ClaimStatus,
  policyID?: string,
  signal?: AbortSignal
): Promise<ClaimSummary[]> => {
  const response = await axios.get(`${apiUrl}/api/claim`, {
    signal,
    params: { companyID: company.id, status, policyID },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response.data;
};

export const fetchClaimsSummary = async (
  companyID: string,
  signal?: AbortSignal
): Promise<DashboardClaimPolicySummary[]> => {
  const response = await axios.get(`${apiUrl}/api/claim/summary`, {
    signal,
    params: { companyID },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response.data;
};

export const fetchClaim = async (
  id: string,
  signal?: AbortSignal
): Promise<Claim> => {
  const response = await axios.get(`${apiUrl}/api/claim/${id}`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response.data;
};

export const fetchClaimAttachment = async (
  id: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/claim/${id}/attachment`, {
    signal,
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response;
};

export const fetchClaimLogAttachment = async (
  id: string,
  logID: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/claim/${id}/attachment/${logID}`,
    {
      signal,
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response;
};

export const fetchClaimAllAttachments = async (
  id: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/claim/${id}/attachment/all`, {
    signal,
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response;
};

export const postClaim = async (
  company: Company,
  body: NewClaimArgs
): Promise<Claim> => {
  const { attachment, contacts, ...otherArgs } = body;
  const cleanedContacts = contacts?.map((contact) =>
    cleanJsonEmptyValues(contact)
  );
  const cleanBody = cleanJsonEmptyValues(otherArgs);

  const response = await axios.post(
    `${apiUrl}/api/claim`,
    {
      ...cleanBody,
      contacts: cleanedContacts,
      companyID: company.id,
      attachment,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const putClaim = async (
  id: string,
  body: UpdateClaimArgs
): Promise<Claim> => {
  const { attachment, contacts, logs, ...otherArgs } = body;
  const cleanedContacts = contacts?.map((contact) =>
    cleanJsonEmptyValues(contact)
  );
  const cleanedLogs = logs?.map((log) =>
    cleanJsonEmptyValues({ ...log, prevAttachment: undefined })
  );
  const cleanBody = cleanJsonEmptyValues(otherArgs);

  const response = await axios.put(
    `${apiUrl}/api/claim/${id}`,
    { ...cleanBody, contacts: cleanedContacts, logs: cleanedLogs, attachment },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const removeClaim = async (id: string) => {
  await axios.delete(`${apiUrl}/api/claim/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
};

export const postClaimConsolidatedReport = async (
  companies: string[],
  periodStart?: Date,
  periodEnd?: Date,
  status?: ClaimStatus
) => {
  const response = await axios.post(
    `${apiUrl}/api/claim/consolidatedReport`,
    { companies, periodStart, periodEnd, status },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
      responseType: 'blob',
    }
  );

  return response;
};
